import React from 'react';
import { Chip } from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';
import {
  diamGridSingleSelectFilterOperators,
  diamGridStringFilterOperators,
} from '../../diamGridUtils';
import type { DiamAuditAction, DiamAuditResourceType } from '../../diamApi';
import {
  formatAuditActionLabel,
  formatAuditResourceTypeLabel,
} from './formatAuditLogLabel';
import type { AuditLogRow } from './types';

function ApplicationNamesCell({ names }: { names: readonly string[] }) {
  if (names.length === 0) {
    return null;
  }

  const label =
    names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;

  return <Chip color="primary" label={label} />;
}

const ACTION_OPTIONS: DiamAuditAction[] = ['CREATE', 'UPDATE', 'DELETE'];

const RESOURCE_TYPE_OPTIONS: DiamAuditResourceType[] = [
  'TEAM',
  'TEAM_MEMBERSHIP',
  'IDENTITY',
  'IDENTITY_DETAILS',
  'INVITATION',
  'REGISTRATION_REQUEST',
  'ROLE_ACCESS_REQUEST',
  'WHITE_LIST_DOMAIN',
  'ROLE',
  'APPLICATION_CONTEXT',
  'TEAM_APPLICATION_CONTEXT',
  'MEMBERSHIP_ROLE_ASSIGNMENT',
];

export function buildAuditLogColumns(
  formatDateTime: (dateIso?: string | null) => string,
): GridColDef<AuditLogRow>[] {
  return [
    {
      field: 'createdDate',
      headerName: 'Date',
      flex: 1,
      minWidth: 180,
      filterable: false,
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row }) => formatDateTime(row.createdDate),
    },
    {
      field: 'createdByName',
      headerName: 'Created by',
      flex: 1.2,
      minWidth: 200,
      filterOperators: diamGridStringFilterOperators,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.7,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: ACTION_OPTIONS.map(action => ({
        value: action,
        label: formatAuditActionLabel(action),
      })),
      filterOperators: diamGridSingleSelectFilterOperators,
      renderCell: ({ row }) => (
        <Chip color="primary" label={formatAuditActionLabel(row.action)} />
      ),
    },
    {
      field: 'resourceType',
      headerName: 'Resource type',
      flex: 1.1,
      minWidth: 200,
      type: 'singleSelect',
      valueOptions: RESOURCE_TYPE_OPTIONS.map(resourceType => ({
        value: resourceType,
        label: formatAuditResourceTypeLabel(resourceType),
      })),
      filterOperators: diamGridSingleSelectFilterOperators,
      renderCell: ({ row }) => formatAuditResourceTypeLabel(row.resourceType),
    },
    {
      field: 'applicationNames',
      headerName: 'Applications',
      flex: 1.2,
      minWidth: 220,
      filterable: false,
      sortable: false,
      valueGetter: (_value, row) => row.applicationNames.join(', '),
      renderCell: ({ row }) => (
        <Tooltip title={row.applicationNames.join(', ')}>
          <span>
            <ApplicationNamesCell names={row.applicationNames} />
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'teamName',
      headerName: 'Team',
      flex: 1,
      minWidth: 180,
      filterable: false,
      renderCell: ({ row }) => <Chip color="primary" label={row.teamName} />,
    },
  ];
}
